import Link from 'next/link';
import { useState } from 'react';
import { ArrowUpRight, Check, Plus, X } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useBasePath } from '../../../../lib/use-base-path';
import { Button, Card, IconButton, Typography } from '../../ui';
import { AgentDot } from '../../../AgentDot';
import { MaestroMark } from '../../MaestroMark';
import { WixWordmark } from '../../connectors/BrandGlyphs';
import styles from './MapInfoCard.module.css';

/** What a selection says: the eyebrow (its region), a title, a line of context, the text, and ONE action. */
export interface MapCardContent {
  /** A stable key for the thing described (a node id, an edge key, an event id). */
  key: string;
  region: string;
  title: string;
  /** A secondary line: the time, the agent, a status such as "Not connected". */
  meta?: string;
  text?: string;
  /** The card's action — every object on the map has one (open, manage, connect, read…). `done` renders it
   *  as already taken (a vote that was cast) with the label it then reads. */
  action?: { label: string; href?: string; onClick?: () => void; done?: boolean };
  /** Extra lines under the text — a phone number, a list of groups — shown as they are. */
  lines?: string[];
  /** A quieter second link, e.g. "Read more" beside a primary "Open task". */
  link?: { label: string; href?: string; onClick?: () => void };
  /**
   * A PROFILE card on the canvas — an agent's (Figma 2032:93672) or a tool's (Figma 2032:93721): its mark and
   * name, an optional role, a rule, what it does, and one full-width outlined button (`action`). `region`,
   * `meta`, `text` and `lines` are not drawn for it; `link`, when there is one, sits under the button.
   */
  profile?: ProfileCard;
}

/** What a profile card shows besides its title. Exactly one mark. */
export interface ProfileCard {
  /** An agent's own dot, 20. */
  agentId?: string;
  /** The Maestro's own mark, 20 — the main agent's card. */
  maestro?: boolean;
  /** A tool's logo on a 28 plate. */
  logoUrl?: string;
  /** A tool drawn by its wordmark instead (Wix). */
  wordmark?: 'wix';
  /** The "+ connect" plate's mark. */
  plus?: boolean;
  /** A folded "+N" plate's card: the count it folds. Its title says it, so it draws no mark. */
  count?: number;
  /** What a folded plate folds, one row each — in place of a description. */
  items?: { name: string; logoUrl?: string }[];
  /** The last row under `items`, when there are more than it lists ("3 more"). */
  more?: string;
  role?: string;
  description?: string;
}

/** A tool's 28 plate: its logo at the frame's 22.56, or its initial if the logo will not load. */
function ProfileLogo({ profile, name, small = false }: { profile: ProfileCard; name: string; small?: boolean }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className={`${styles.profilePlate}${small ? ` ${styles.profilePlateSmall}` : ''}`} aria-hidden>
      {profile.plus ? (
        <Plus size={16} strokeWidth={1.5} absoluteStrokeWidth />
      ) : profile.wordmark === 'wix' ? (
        <WixWordmark width={18} />
      ) : profile.logoUrl && !failed ? (
        <img src={profile.logoUrl} alt="" width={small ? 16 : 22.56} height={small ? 16 : 22.56} onError={() => setFailed(true)} />
      ) : (
        <Typography variant="tiny-semi-bold" component="span" color="primary">
          {name.charAt(0).toUpperCase()}
        </Typography>
      )}
    </span>
  );
}

/**
 * The info card that opens on a node, a wire or a particle (Figma "Info card", 491:196873): a small DS Card
 * floated beside the thing, naming it, what last happened there, and what the reader can do about it. HTML
 * over the canvas rather than a `<foreignObject>`, so it keeps the app's type and never scales with the zoom.
 */
export function MapInfoCard({
  content,
  anchor,
  viewport,
  onClose,
}: {
  content: MapCardContent;
  anchor: { x: number; y: number };
  viewport: { w: number; h: number };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const W = 272;
  const H = 190;
  // Beside the anchor, flipping to whichever side has room; clamped inside the host.
  const left = anchor.x + 28 + W <= viewport.w ? anchor.x + 28 : Math.max(8, anchor.x - 28 - W);
  const top = Math.min(Math.max(8, anchor.y - H / 2), Math.max(8, viewport.h - H - 8));
  const hrefOf = (h?: string) => (h ? (h.startsWith('http') ? h : `${basePath}${h}`) : undefined);

  if (content.profile) {
    // Figma 2032:93672 / 2032:93721: 265 wide, the S card's 16 radius, 20 in; head · 14 · rule · 14 · text ·
    // 16 · button; the close a 42 icon button 6 in from the top-end corner.
    const PW = 265;
    const pLeft = anchor.x + 28 + PW <= viewport.w ? anchor.x + 28 : Math.max(8, anchor.x - 28 - PW);
    const profile = content.profile;
    return (
      <Card
        size="sm"
        className={`${styles.card} ${styles.profileCard}`}
        style={{ left: pLeft, top, width: PW }}
        role="group"
        aria-label={content.title}
        data-node={content.key}
      >
        <IconButton size="large" variant="text" className={styles.profileClose} onClick={onClose} aria-label={t('common.close')}>
          <X size={20} aria-hidden />
        </IconButton>
        <div className={styles.profileWho}>
          <div className={styles.profileName}>
            {profile.count ? null : profile.maestro ? (
              <MaestroMark size={20} />
            ) : profile.agentId ? (
              <AgentDot id={profile.agentId} size={20} />
            ) : (
              <ProfileLogo profile={profile} name={content.title} />
            )}
            <Typography variant="h2" color="primary" component="h3" className={styles.profileTitle}>
              {content.title}
            </Typography>
          </div>
          {profile.role && (
            <Typography variant="small-medium" color="secondary" component="p" className={styles.profileRole}>
              {profile.role}
            </Typography>
          )}
        </div>
        <hr className={styles.profileRule} />
        {profile.description && (
          <Typography variant="heading-04" color="primary" component="p" className={styles.profileText} dir="auto">
            {profile.description}
          </Typography>
        )}
        {profile.items && (
          <ul className={styles.profileList}>
            {profile.items.map((item) => (
              <li key={item.name} className={styles.profileItem}>
                <ProfileLogo profile={{ logoUrl: item.logoUrl }} name={item.name} small />
                <Typography variant="heading-04" color="primary" component="span" noWrap dir="auto">
                  {item.name}
                </Typography>
              </li>
            ))}
            {profile.more && (
              <li className={styles.profileItem}>
                <Typography variant="p2" color="secondary" component="span">
                  {profile.more}
                </Typography>
              </li>
            )}
          </ul>
        )}
        {/* One line is the site's address, which may take two; several are the other sites, one line each. */}
        {content.lines?.map((line, _, all) => (
          <Typography
            key={line}
            variant="p3"
            color="secondary"
            component="p"
            noWrap={all.length > 1}
            className={`${styles.profileLine}${all.length > 1 ? '' : ` ${styles.profileLineWrap}`}`}
            dir="auto"
          >
            {line}
          </Typography>
        ))}
        {content.action?.href && (
          <Button variant="outlined" size="medium" fullWidth href={content.action.href} onClick={content.action.onClick} className={styles.profileButton}>
            {content.action.label}
          </Button>
        )}
        {content.link?.href?.startsWith('http') && (
          <a href={content.link.href} target="_blank" rel="noopener noreferrer" className={`${styles.more} ${styles.profileLink}`} onClick={content.link.onClick}>
            {content.link.label}
            <ArrowUpRight size={14} aria-hidden />
          </a>
        )}
      </Card>
    );
  }

  return (
    <Card className={styles.card} style={{ left, top, width: W }} role="group" aria-label={content.title} data-node={content.key}>
      <div className={styles.head}>
        <Typography variant="p3" color="secondary" className={styles.region}>
          {content.region}
        </Typography>
        <IconButton size="tiny" variant="text" onClick={onClose} aria-label={t('common.close')}>
          <X size={16} aria-hidden />
        </IconButton>
      </div>
      <Typography variant="h4" color="primary" component="h3" className={styles.title}>
        {content.title}
      </Typography>
      {content.meta && (
        <Typography variant="p3" color="secondary" className={styles.meta}>
          {content.meta}
        </Typography>
      )}
      {/* Only what happened, when something did: a placeholder sentence on every quiet card said nothing. */}
      {content.text ? (
        <Typography variant="p2" color="primary" className={styles.text} dir="auto">
          {content.text}
        </Typography>
      ) : null}
      {content.lines?.length ? (
        <ul className={styles.lines}>
          {content.lines.map((line, i) => (
            <li key={i}>
              <Typography variant="p3" color="secondary" component="span" dir="auto">
                {line}
              </Typography>
            </li>
          ))}
        </ul>
      ) : null}
      <div className={styles.actions}>
        {content.action?.done ? (
          <Button size="small" variant="outlined" disabled startIcon={<Check size={16} aria-hidden />}>
            {content.action.label}
          </Button>
        ) : null}
        {content.action &&
          !content.action.done &&
          (content.action.href ? (
            <Button
              size="small"
              href={content.action.href}
              // Anything that is not an in-app path (https, sms:, wa.me) is external and leaves in a new tab.
              external={!content.action.href.startsWith('/')}
              {...(!content.action.href.startsWith('/') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              onClick={content.action.onClick}
            >
              {content.action.label}
            </Button>
          ) : (
            <Button size="small" onClick={content.action.onClick}>
              {content.action.label}
            </Button>
          ))}
        {content.link &&
          // An outside address (a site's own URL) opens in a new tab, as the action button above does for one;
          // an in-app route stays a Next link.
          (content.link.href?.startsWith('http') ? (
            <a href={content.link.href} target="_blank" rel="noopener noreferrer" className={styles.more} onClick={content.link.onClick}>
              {content.link.label}
              <ArrowUpRight size={14} aria-hidden />
            </a>
          ) : (
            <Link href={content.link.href ?? '#'} as={hrefOf(content.link.href)} className={styles.more} onClick={content.link.onClick}>
              {content.link.label}
              <ArrowUpRight size={14} aria-hidden />
            </Link>
          ))}
      </div>
    </Card>
  );
}
